import type { CaseStudy } from '../types';

export const aiCoding: CaseStudy = {
  id: 'ai-coding',
  order: 11,
  title: 'AI 编程',
  english: 'AI-Assisted Software',
  category: 'technology',
  era: '2021 — 今',
  region: '全球',
  accent: '#367a6a',
  hook: '生成代码越来越便宜，但“确认它是对的、能维护、能负责”没有同步变便宜。价值正在从打字速度，迁移到定义问题与验收结果。',
  question: '当 AI 能写越来越多代码，为什么有些开发者反而更慢？',
  summary:
    'AI 降低了代码生成的边际成本，却把瓶颈推向需求澄清、代码审查、测试、安全与系统集成。生产率提升高度依赖任务可分解性、上下文和验证能力；同样的工具，在不同任务上可能加速，也可能拖慢。',
  thesis: '当生成变便宜，稀缺的不是代码，而是「可验证的正确性」与「承担结果的能力」。',
  quote: 'AI 让写代码变便宜，但没有让“知道自己写对了”变便宜。',
  isNew: true,
  entryNodeIds: ['ai-codecost', 'ai-verification', 'ai-bottleneck'],
  focusNodeId: 'ai-verification',
  lensIds: ['standards', 'scarcity', 'asymmetry', 'incentives'],
  opportunityIds: ['opp-ai-verification', 'opp-vertical-ai'],
  sourceIds: ['copilot-arxiv', 'metr-2025'],
  facts: [
    {
      text: 'GitHub Copilot 在 2021 年进入技术预览、2022 年正式商用；随后生成式 AI 编程工具迅速普及。',
      sourceIds: ['copilot-arxiv'],
    },
    {
      text: '一项针对特定任务的受控研究发现，使用 GitHub Copilot 的开发者完成该任务平均快约 55%；这个结果不能直接外推到所有开发工作。',
      sourceIds: ['copilot-arxiv'],
    },
    {
      text: 'METR 2025 年发布的实地研究显示，16 名资深开源开发者在自己的仓库中使用早期 2025 年 AI 工具时，完成任务平均反而慢约 19%，尽管他们主观上觉得自己更快。',
      sourceIds: ['metr-2025'],
    },
  ],
  nodes: [
    {
      id: 'aicoding',
      caseId: 'ai-coding',
      kind: 'case',
      stage: 0,
      lane: 0,
      title: 'AI 编程',
      summary: '代码生成成本骤降，验证与集成成为新瓶颈。',
      detail:
        'AI 编程工具从自动补全发展到对话式生成、重构、测试与调试。它们能显著降低样板代码和小任务的成本，但在大型、陌生、强约束的代码库中，理解上下文、验证正确性和承担后果仍然依赖人。生产率变化因此高度不均匀。',
      boundary: '本案例讨论辅助编程工具，不假设通用人工智能已经取代软件工程。',
      evidence: 'fact',
      confidence: 'high',
      sourceIds: ['copilot-arxiv', 'metr-2025'],
    },
    {
      id: 'ai-codecost',
      caseId: 'ai-coding',
      kind: 'mechanism',
      stage: 1,
      lane: -1,
      title: '生成成本下降',
      summary: '样板代码、测试骨架、文档和常见模式的生产时间被大幅压缩。',
      detail:
        '大模型可以根据自然语言和上下文生成代码，降低重复劳动与起步成本。对于边界清晰、可验证的小任务，收益直接；对于依赖大量隐性知识和历史决策的系统，生成只是第一步。',
      boundary: '生成速度不等于交付速度；代码还要被理解、审查、测试、部署和维护。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['copilot-arxiv'],
    },
    {
      id: 'ai-verification',
      caseId: 'ai-coding',
      kind: 'mechanism',
      stage: 1,
      lane: 1,
      title: '验证瓶颈：正确性没有自动打折',
      summary: 'AI 产出的代码看起来合理，但错误更隐蔽，审查者要付出更高认知成本。',
      detail:
        '生成代码的语法通常没问题，错误更多出现在边界条件、权限、并发、数据模型和安全假设上。审查一段“看起来对”的代码，比自己写更难，因为审查者要先理解生成者的隐含假设。测试、静态分析和可追溯的需求因此变得更重要。',
      boundary: '在测试覆盖高、任务可独立验证的场景，验证成本会低很多。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['metr-2025'],
    },
    {
      id: 'ai-bottleneck',
      caseId: 'ai-coding',
      kind: 'mechanism',
      stage: 2,
      lane: -1,
      title: '瓶颈迁移：从写代码到定义问题',
      summary: '当生成变便宜，需求澄清、架构选择、验收标准与责任归属成为稀缺环节。',
      detail:
        '软件交付的总时间包括理解需求、设计、编码、审查、测试、部署和运维。编码时间下降后，其他环节占比上升。能清楚定义“什么算完成”、写出可执行验收标准的人，会比单纯写得快的人更有价值。',
      boundary: '在原型、一次性脚本和低风险场景，编码仍是主要瓶颈，生成工具可以直接加速。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['metr-2025'],
    },
    {
      id: 'ai-org',
      caseId: 'ai-coding',
      kind: 'mechanism',
      stage: 2,
      lane: 1,
      title: '组织采用：感觉变快，不等于交付变快',
      summary: '个人主观效率、团队吞吐与业务结果，是三件不同的事。',
      detail:
        '开发者可能感觉 AI 帮了大忙，但代码评审、返工、依赖升级和安全审查会吃掉收益。组织若只用“生成行数”或“使用率”衡量，容易奖励表演式采用。真正的指标应是交付周期、缺陷率、变更失败率与维护成本。',
      boundary: '不同团队、代码库和任务类型的差异很大；不能用一个平均数字做人员考核。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['metr-2025'],
    },
    {
      id: 'ai-condition',
      caseId: 'ai-coding',
      kind: 'condition',
      stage: 2,
      lane: 0,
      title: '前提：可测试、可分解、有上下文',
      summary: '任务越容易验收、上下文越清晰，AI 的净收益越大。',
      detail:
        'AI 在独立函数、测试、数据转换、前端组件等边界清晰的任务上表现更好；在遗留系统、跨服务重构、强合规场景中，隐性约束多，生成结果的验证成本高。工具效果取决于工作流，而不只取决于模型。',
      boundary: '上下文窗口、代码库规模、团队规范与安全要求都会改变结果。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['metr-2025'],
    },
    {
      id: 'ai-outcome',
      caseId: 'ai-coding',
      kind: 'outcome',
      stage: 3,
      lane: 0,
      title: '结果：更多软件，更不均匀的生产率',
      summary: '软件供给增加，原型更快；但价值向领域知识、分发与可信交付集中。',
      detail:
        '当写代码更便宜，市场会出现更多小工具、自动化和定制软件。竞争不会只奖励“会用 AI”，而会奖励能选对问题、拿到分发、保证质量并承担结果的人。部分重复性岗位被压缩，同时出现新的验证、集成与运营需求。',
      boundary: '“更多软件”不等于“更多价值”；需求、分发和维护能力仍然约束市场。',
      evidence: 'inference',
      confidence: 'medium',
      sourceIds: ['metr-2025', 'copilot-arxiv'],
    },
    {
      id: 'ai-risk',
      caseId: 'ai-coding',
      kind: 'risk',
      stage: 4,
      lane: -1,
      title: '脆弱处：看似正确的错误',
      summary: 'AI 生成的代码可能引入安全漏洞、隐性依赖与维护债务，而审查者更容易放过它。',
      detail:
        '流畅的解释和完整的代码会制造“已经理解”的错觉。风险包括：过时 API、许可证问题、数据泄露、权限绕过、测试只覆盖 happy path。组织需要把 AI 输出当作“初稿”，而不是“结论”。',
      boundary: '成熟的安全扫描、测试与人工复核可以降低风险，但不能把责任转移给模型。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['metr-2025'],
    },
    {
      id: 'ai-opp',
      caseId: 'ai-coding',
      kind: 'opportunity',
      stage: 4,
      lane: 1,
      title: '机会透镜：卖“验证过的结果”',
      summary: '不要只卖 AI 工具或按小时写代码，要卖可验收的业务结果。',
      detail:
        '可行的方向：为中小企业做窄场景自动化并按结果收费；为团队做 AI 代码审查、安全与合规；把行业知识封装成带验收标准的垂直工作流；培训“会定义问题、会验收”的复合人才。核心资产是领域知识、测试用例、客户信任与责任承担。',
      boundary: '结果导向服务需要清晰的范围、验收标准与责任上限；模型或工具会迅速商品化。',
      evidence: 'hypothesis',
      confidence: 'medium',
      sourceIds: ['metr-2025', 'copilot-arxiv'],
    },
  ],
  edges: [
    {
      id: 'ai-e1',
      caseId: 'ai-coding',
      from: 'aicoding',
      to: 'ai-codecost',
      label: '生成变便宜',
      explanation: '大模型降低了样板代码与常见模式的生产时间。',
      tone: 'cause',
    },
    {
      id: 'ai-e2',
      caseId: 'ai-coding',
      from: 'aicoding',
      to: 'ai-verification',
      label: '输出需要验收',
      explanation: '生成代码必须被理解、测试与审查，错误可能更隐蔽。',
      tone: 'cause',
    },
    {
      id: 'ai-e3',
      caseId: 'ai-coding',
      from: 'ai-codecost',
      to: 'ai-bottleneck',
      label: '瓶颈迁移',
      explanation: '编码时间下降后，需求澄清、架构和验收成为新的稀缺环节。',
      tone: 'cause',
    },
    {
      id: 'ai-e4',
      caseId: 'ai-coding',
      from: 'ai-verification',
      to: 'ai-bottleneck',
      label: '审查成本上升',
      explanation: '审查“看起来对”的代码需要更多上下文与判断，验证成为交付瓶颈。',
      tone: 'cause',
    },
    {
      id: 'ai-e5',
      caseId: 'ai-coding',
      from: 'ai-codecost',
      to: 'ai-outcome',
      label: '供给增加',
      explanation: '更便宜的生成让更多软件、原型和自动化项目变得可行。',
      tone: 'cause',
    },
    {
      id: 'ai-e6',
      caseId: 'ai-coding',
      from: 'ai-bottleneck',
      to: 'ai-outcome',
      label: '价值重新分配',
      explanation: '能定义问题、设计验收并承担结果的人，获得更高相对价值。',
      tone: 'cause',
    },
    {
      id: 'ai-e7',
      caseId: 'ai-coding',
      from: 'ai-org',
      to: 'ai-outcome',
      label: '采用方式决定收益',
      explanation: '组织是否重构评审、测试与指标，决定 AI 收益能否转化为交付。',
      tone: 'cause',
    },
    {
      id: 'ai-e8',
      caseId: 'ai-coding',
      from: 'ai-condition',
      to: 'ai-outcome',
      label: '适用条件',
      explanation: '可测试、可分解、上下文清晰的任务，更容易获得净收益。',
      tone: 'enable',
    },
    {
      id: 'ai-e9',
      caseId: 'ai-coding',
      from: 'ai-outcome',
      to: 'ai-risk',
      label: '质量债务',
      explanation: '更快的生成可能积累安全、依赖与维护问题，延迟暴露。',
      tone: 'risk',
    },
    {
      id: 'ai-e10',
      caseId: 'ai-coding',
      from: 'ai-risk',
      to: 'ai-opp',
      label: '验证成为产品',
      explanation: '当生成泛滥，能证明“结果正确、安全、可维护”的服务更有价值。',
      tone: 'enable',
    },
  ],
};
